import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from '@/components/ui/toast'
import { AttachmentPreviewGrid } from '@/components/shared/AttachmentPreviewGrid'
import { FileUploadDropzone } from '@/components/shared/FileUploadDropzone'
import { ConfirmDeleteDialog } from '@/components/shared'
import type { AttachmentRef } from '@/components/shared/types'
import {
  useDeleteUserDocument,
  useRenameUserDocument,
  useUserDocuments,
} from '@/hooks/useUsers'
import { DOCUMENT_FILE_ACCEPT, isDocumentFileExtension } from '@/lib/constants'
import { fileNameExtension } from '@/lib/documentRename'
import { getFileSizeLimitMb, isFileTooLarge } from '@/lib/uploadValidation'
import { getApiErrorMessage } from '@/services/api'
import type { Documentable } from '@/types'

export interface PendingUserDocument {
  key: string
  file: File
}

interface UserDocumentsSectionProps {
  userId?: string | null
  readOnly?: boolean
  disabled?: boolean
  pendingFiles: PendingUserDocument[]
  onPendingChange: (next: PendingUserDocument[]) => void
}

// 2.11.2 — Documents section on Create/Edit/View user.
// Create/Edit: stage new files locally until Save; View: download only.
export function UserDocumentsSection({
  userId,
  readOnly = false,
  disabled = false,
  pendingFiles,
  onPendingChange,
}: UserDocumentsSectionProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const { t } = useTranslation('settings')
  const [deleteTarget, setDeleteTarget] = useState<Documentable | null>(null)
  const [dragActive, setDragActive] = useState(false)
  const { data, isLoading, isError } = useUserDocuments(userId ?? undefined, {
    limit: 50,
  })
  const remove = useDeleteUserDocument(userId ?? undefined)
  const rename = useRenameUserDocument(userId ?? undefined)

  const rows = data?.data ?? []
  const canMutate = !readOnly && !disabled
  const busy = remove.isPending || rename.isPending

  async function uploadFiles(files: FileList | File[]) {
    if (files.length === 0 || busy) return

    const validFiles: File[] = []
    for (const file of Array.from(files)) {
      const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
      if (!isDocumentFileExtension(ext)) {
        toast({
          variant: 'destructive',
          title: t('users.documents.unsupportedFormat'),
        })
        continue
      }

      if (isFileTooLarge(file)) {
        toast({
          variant: 'destructive',
          title: t('shared:attachFiles.tooLarge', {
            max: getFileSizeLimitMb(),
          }),
        })
        continue
      }
      validFiles.push(file)
    }

    if (validFiles.length === 0) return

    const batchId = Date.now()
    onPendingChange([
      ...pendingFiles,
      ...validFiles.map((file, index) => ({
        key: `${file.name}-${file.size}-${file.lastModified}-${batchId}-${index}`,
        file,
      })),
    ])
  }

  function onFile(event: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? [])
    event.target.value = ''
    if (files.length > 0) void uploadFiles(files)
  }

  async function confirmDelete() {
    if (!deleteTarget || !userId) return
    try {
      await remove.mutateAsync(deleteTarget.id)
      toast({
        variant: 'success',
        title: t('users.documents.deleteSuccess'),
      })
      setDeleteTarget(null)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(err, t('users.documents.deleteError')),
      })
    }
  }

  function removePending(key: string) {
    onPendingChange(pendingFiles.filter(file => file.key !== key))
  }

  async function renameDocument(item: AttachmentRef, name: string) {
    const nextName = `${name}${fileNameExtension(item.name)}`

    if (item.file) {
      onPendingChange(
        pendingFiles.map(pending =>
          pending.key === item.id
            ? {
                ...pending,
                file: new File([pending.file], nextName, {
                  type: pending.file.type,
                  lastModified: pending.file.lastModified,
                }),
              }
            : pending
        )
      )
      return
    }

    if (!userId || !item.id) return
    await rename.mutateAsync({ documentId: item.id, name })
    toast({
      variant: 'success',
      title: t('users.documents.renameSuccess'),
    })
  }

  const persistedItems: AttachmentRef[] = rows.map(doc => ({
    id: doc.id,
    name: doc.file_name,
    url: doc.url,
    mime_type: doc.file_type,
    size: doc.file_size,
    created_at: doc.created_at,
    source: 'upload',
  }))

  const pendingItems: AttachmentRef[] = pendingFiles.map(file => ({
    id: file.key,
    name: file.file.name,
    mime_type: file.file.type,
    size: file.file.size,
    created_at: new Date(file.file.lastModified).toISOString(),
    source: 'upload',
    file: file.file,
  }))

  const cards = [...persistedItems, ...pendingItems]

  return (
    <section className='rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_1px_4px_rgba(38,45,66,0.05),0_8px_16px_-12px_rgba(38,45,66,0.1)]'>
      <header className='flex min-w-0 items-center gap-3 px-4 pb-2 pt-4 sm:px-6'>
        <span className='inline-flex size-6 shrink-0 items-center justify-center rounded-lg bg-[#ff5a1f] text-xs font-medium text-white'>
          3
        </span>
        <h2 className='min-w-0 font-heading text-sm font-semibold leading-[1.2] text-[#171a20]'>
          {t('users.documents.title')}
        </h2>
      </header>

      <div className='flex flex-col gap-4 px-4 pb-4 pt-2 sm:px-6 sm:pb-6'>
        {canMutate && (
          <>
            <input
              ref={inputRef}
              type='file'
              multiple
              className='hidden'
              accept={DOCUMENT_FILE_ACCEPT}
              disabled={busy}
              onChange={onFile}
            />
            <FileUploadDropzone
              disabled={busy}
              onClick={() => inputRef.current?.click()}
              dragActive={dragActive}
              onDragOver={event => {
                event.preventDefault()
                setDragActive(true)
              }}
              onDragLeave={event => {
                event.preventDefault()
                setDragActive(false)
              }}
              onDrop={event => {
                event.preventDefault()
                setDragActive(false)
                if (!busy && event.dataTransfer.files.length > 0) {
                  void uploadFiles(event.dataTransfer.files)
                }
              }}
              primaryText={t('shared:attachFiles.uploadHintPrimary')}
              secondaryText={t('shared:attachFiles.uploadHintFormats')}
              loading={busy}
              loadingText={t('shared:attachFiles.uploading')}
            />
          </>
        )}

        {userId && isLoading ? (
          <div className='grid gap-4 sm:grid-cols-2'>
            <Skeleton className='h-[190px] rounded-3xl' />
            <Skeleton className='h-[190px] rounded-3xl' />
          </div>
        ) : null}

        {userId && isError && !isLoading ? (
          <p className='rounded-xl border border-dashed border-[#f0c7c4] bg-[#fff7f7] px-4 py-5 text-sm text-destructive'>
            {t('users.documents.loadError')}
          </p>
        ) : null}

        {!isLoading && !isError && cards.length > 0 ? (
          <AttachmentPreviewGrid
            items={cards}
            typeBadgeClassName='font-medium'
            removeLabel={t('users.documents.actions.delete')}
            onRename={canMutate ? renameDocument : undefined}
            onRemove={
              canMutate
                ? item => {
                    const isPendingItem = pendingItems.some(
                      pending => pending.id === item.id
                    )
                    if (isPendingItem) {
                      removePending(item.id as string)
                      return
                    }
                    const target = rows.find(doc => doc.id === item.id)
                    if (target) setDeleteTarget(target)
                  }
                : undefined
            }
            disabled={busy}
            onItemClick={item => {
              if (item.url) {
                window.open(item.url, '_blank', 'noopener')
              }
            }}
            itemClickLabel={t('users.documents.actions.download')}
          />
        ) : null}

        {!isLoading && !isError && cards.length === 0 ? (
          <p className='rounded-xl border border-dashed border-[#d9e2ec] bg-[#f8f9fb] px-4 py-6 text-sm text-[#6c7285]'>
            {t('users.documents.empty')}
          </p>
        ) : null}
      </div>

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={open =>
          !open && !remove.isPending && setDeleteTarget(null)
        }
        title={t('users.documents.deleteDialog.title')}
        description={t('users.documents.deleteDialog.message')}
        confirmLabel={t('users.documents.deleteDialog.confirm')}
        onConfirm={confirmDelete}
        busy={remove.isPending}
      />
    </section>
  )
}

export default UserDocumentsSection
